import React, { ReactNode } from 'react';
import './FeedbackPageLayout.css';

interface FeedbackPageLayoutProps {
  children: ReactNode;
  className?: string;
}

interface FeedbackMainLayoutProps {
  leftColumn?: ReactNode;
  rightColumn?: ReactNode;
  showMainLayout?: boolean;
  children?: ReactNode;
  rightColumnRef?: React.RefObject<HTMLDivElement | null>;
}

interface FeedbackSectionProps {
  children: ReactNode;
  className?: string;
}

interface FeedbackMessageProps {
  type: 'success' | 'error';
  children: ReactNode;
}

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  title?: string;
  description?: string;
}

// Main page container
export const FeedbackPageLayout: React.FC<FeedbackPageLayoutProps> = ({ 
  children, 
  className = '' 
}) => {
  return (
    <div className={`feedback-page-layout ${className}`}>
      {children}
    </div>
  );
};

// Main content layout with left/right columns
export const FeedbackMainLayout: React.FC<FeedbackMainLayoutProps> = ({ 
  leftColumn, 
  rightColumn, 
  showMainLayout = true,
  children,
  rightColumnRef
}) => {
  if (!showMainLayout) {
    return <>{children}</>;
  }

  return (
    <div className="feedback-main-content-layout">
      {leftColumn && (
        <div className="feedback-left-column">
          {leftColumn}
        </div>
      )}
      {rightColumn && (
        <div className="feedback-right-column" ref={rightColumnRef}>
          {rightColumn}
        </div>
      )}
      {children}
    </div>
  );
};

// Generic section container
export const FeedbackSection: React.FC<FeedbackSectionProps> = ({ 
  children, 
  className = '' 
}) => {
  return (
    <div className={`feedback-section ${className}`}>
      {children}
    </div>
  );
};

// Success/Error message component
export const FeedbackMessage: React.FC<FeedbackMessageProps> = ({ 
  type, 
  children 
}) => {
  return (
    <div className={`feedback-message feedback-message--${type}`}>
      {children}
    </div>
  );
};

// Modal wrapper component
export const FeedbackModal: React.FC<FeedbackModalProps> = ({ 
  isOpen, 
  onClose, 
  children, 
  title, 
  description 
}) => {
  if (!isOpen) return null;

  return (
    <div className="feedback-modal-overlay" onClick={onClose}>
      <div className="feedback-modal" onClick={(e) => e.stopPropagation()}>
        {(title || description) && (
          <div className="feedback-modal-header">
            {title && <h2>{title}</h2>}
            {description && <p>{description}</p>}
            <button 
              className="feedback-modal-close-button"
              onClick={onClose}
            >
              ✕
            </button>
          </div>
        )}
        <div className="feedback-modal-content">
          {children}
        </div>
      </div>
    </div>
  );
};

// Empty state component
export const FeedbackEmptyState: React.FC<{ 
  title: string; 
  description?: string; 
  action?: ReactNode;
}> = ({ title, description, action }) => {
  return (
    <div className="feedback-empty-state">
      <h3>{title}</h3>
      {description && <p>{description}</p>}
      {action && <div className="feedback-empty-state-action">{action}</div>}
    </div>
  );
};